import { parseDataset } from '@rawgraphs/rawgraphs-core'
import { get } from 'lodash'
import { useCallback, useState } from 'react'
import { localeList, WEBWORKER_ACTIVE } from '../constants'
import { parseDatasetInWorker } from '../worker'
import { enhanceInferredDateTypes } from '../services/dateTypeInference'

export default function useDataParser() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const parseDatasetAsync = useCallback((rawData, dataTypes, parsingOptions) => {
    setLoading(true)
    return parseDatasetInWorker(rawData, dataTypes, {
      ...parsingOptions,
      dateLocale: get(localeList, parsingOptions.locale),
    })
      .then((result) => result)
      .catch((err) => {
        console.error('Parse worker error:', err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const parseDatasetSync = useCallback((rawData, dataTypes, parsingOptions) => {
    setLoading(true)
    return new Promise((resolve, reject) => {
      try {
        const result = parseDataset(rawData, dataTypes, {
          ...parsingOptions,
          dateLocale: get(localeList, parsingOptions.locale),
        })
        resolve(result)
      } catch (e) {
        reject(e)
      } finally {
        setLoading(false)
      }
    })
  }, [])

  const parseDatasetAuto = useCallback(
    async (rawData, dataTypes, parsingOptions) => {
      const parse = WEBWORKER_ACTIVE ? parseDatasetAsync : parseDatasetSync
      const result = await parse(rawData, dataTypes, parsingOptions)

      if (dataTypes || !result?.dataTypes) {
        return result
      }

      const enhancedDataTypes = enhanceInferredDateTypes(
        rawData,
        result.dataTypes,
        get(localeList, parsingOptions.locale)
      )

      if (enhancedDataTypes === result.dataTypes) {
        return result
      }

      return parse(rawData, enhancedDataTypes, parsingOptions)
    },
    [parseDatasetAsync, parseDatasetSync]
  )

  const parseDatasetAndSetData = useCallback(
    (rawData, dataTypes, parsingOptions) => {
      return parseDatasetAuto(rawData, dataTypes, parsingOptions).then(
        (result) => setData(result)
      )
    },
    [parseDatasetAuto]
  )

  return { data, setData, loading, parseDatasetAuto, parseDatasetAndSetData }
}
